import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, Undo2, Loader2, Download, History } from "lucide-react";
import { toast } from "sonner";
import { useActiveDataset } from "@/contexts/ActiveDatasetContext";
import { useDatasetVersions } from "@/hooks/useDatasetVersions";
import { rowsToCSV } from "@/lib/cleaning-engine";

/**
 * Apply / Revert act on the shared working dataset and persist real versions
 * in `dataset_versions`. No-ops and console logs are gone: if there is nothing
 * to apply, the buttons say so.
 */
export const CleaningActions = () => {
  const { dataset, updateRows, markSaved } = useActiveDataset();
  const { versions, load, saveVersion, revertToPrevious, isBusy } = useDatasetVersions(
    dataset?.key,
  );
  const [action, setAction] = useState<"apply" | "revert" | null>(null);

  useEffect(() => {
    if (dataset?.key) load(dataset.key).catch(() => undefined);
  }, [dataset?.key, load]);

  const handleApply = async () => {
    if (!dataset) return;
    setAction("apply");
    try {
      const version = await saveVersion({
        datasetKey: dataset.key,
        label: dataset.label,
        rows: dataset.rows,
        changeSummary: { changes: dataset.pendingChanges },
      });
      markSaved(version.version);
      toast.success(`Saved version ${version.version} — ${version.row_count} rows`);
    } catch (e: any) {
      toast.error(e?.message || "Could not save your changes");
    } finally {
      setAction(null);
    }
  };

  const handleRevert = async () => {
    if (!dataset) return;
    setAction("revert");
    try {
      const previous = await revertToPrevious(dataset.key);
      updateRows(previous.rows, []);
      markSaved(previous.version);
      toast.success(`Went back to version ${previous.version}`);
    } catch (e: any) {
      toast.error(e?.message || "Nothing to revert to");
    } finally {
      setAction(null);
    }
  };

  const handleDownload = () => {
    if (!dataset) return;
    const blob = new Blob([rowsToCSV(dataset.rows)], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${dataset.key}-cleaned.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (!dataset) {
    return (
      <Card className="p-4">
        <h3 className="font-medium">Save your cleaned data</h3>
        <p className="text-sm text-muted-foreground">
          Load a file in the cleaning tools above, then come back here to save or
          undo your changes.
        </p>
      </Card>
    );
  }

  return (
    <Card className="p-4">
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div className="space-y-1">
          <h3 className="font-medium flex items-center gap-2">
            {dataset.label}
            {dataset.savedVersion !== null && (
              <Badge variant="secondary">v{dataset.savedVersion}</Badge>
            )}
            {dataset.dirty && <Badge variant="outline">Unsaved changes</Badge>}
          </h3>
          <p className="text-sm text-muted-foreground">
            {dataset.rows.length} rows · {dataset.columns.length} columns
            {versions.length > 0 && (
              <span className="inline-flex items-center gap-1 ml-2">
                <History className="h-3 w-3" />
                {versions.length} saved version{versions.length === 1 ? "" : "s"}
              </span>
            )}
          </p>
          {dataset.pendingChanges.length > 0 && (
            <ul className="text-xs text-muted-foreground list-disc ml-4 mt-1">
              {dataset.pendingChanges.slice(-4).map((c, i) => (
                <li key={i}>
                  {c.action} ({c.rows_affected})
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex gap-3">
          <Button variant="ghost" onClick={handleDownload} disabled={!dataset.rows.length}>
            <Download className="mr-2 h-4 w-4" />
            Download CSV
          </Button>
          <Button
            variant="outline"
            onClick={handleRevert}
            disabled={isBusy || versions.length < 2}
          >
            {action === "revert" ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Undo2 className="mr-2 h-4 w-4" />
            )}
            Revert
          </Button>
          <Button onClick={handleApply} disabled={isBusy || !dataset.rows.length}>
            {action === "apply" ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Check className="mr-2 h-4 w-4" />
            )}
            Save changes
          </Button>
        </div>
      </div>
    </Card>
  );
};
